import { FC, useEffect, useState, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import { eachDayOfInterval, startOfMonth, endOfMonth, getDay } from 'date-fns';

import API from 'core/Api';
import { buildClientEntity } from 'utils/helpers/client';
import { PermissionKeys } from 'store/types';
import { useLanguageContext } from 'store/language';
import { usePermissionsContext } from 'store/permissions';

import { getUserInfo } from 'utils/auth';
import { getSubdomain } from 'utils/helpers/routing';
import { getIsLeaveFieldDirty, setLeaveDaysValue } from 'utils/helpers/leaves';
import { getLeaveYears } from 'utils/helpers/formatters';
import { useModal } from 'store/modal';

import Form from 'components/form/Form';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import FormGroup from 'components/form/FormGroup';
import FormRowGroup from 'components/form/FormRowGroup';
import { ILeaveData } from 'components/leavescalendargrid';
import { ISelectOptionProps } from 'components/form/types';
import nkpdCodes from '../nkpd.codes.json';
import { IInputs, IDropdowns } from './types';

interface IProps {
  methods: any;
  handleSubmit: (data: IInputs) => void;
  btnLabel: string;
  title?: string;
  roleOptions: ISelectOptionProps[];
  userDataOptions: IDropdowns;
  isEdit?: boolean;
  client?: any;
  setIsOrganizeGroup?: (value: boolean) => void;
  setIsManageGroupRoleLimits?: (value: boolean) => void;
  hasHiddenSensitiveFields?: boolean;
}

type CodeTuple = [string, string];

const EmployeeForm: FC<IProps> = ({
  methods,
  handleSubmit,
  btnLabel,
  title,
  roleOptions,
  userDataOptions,
  isEdit,
  client,
  setIsOrganizeGroup,
  setIsManageGroupRoleLimits,
  hasHiddenSensitiveFields,
}: IProps) => {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { checkPermission } = usePermissionsContext();
  const { control, watch, unregister, register, setValue } = methods;
  const { showModal, hideModal } = useModal();

  const [dateH, setDateH] = useState<Date>(new Date());
  const year = dateH.getFullYear();

  const {
    isLoading: isLoadingHolidays,
    error: errorHolidays,
    data: holidaysData = [],
  } = useQuery<ILeaveData[], Error>([buildClientEntity('holidays', client), year], (): Promise<ILeaveData[]> => {
    return API.get(`holidays/${year}`).then(res => {
      return res.data?.items?.map((holiday: any, index: number) => {
        // Set the start time to 00:00:00
        const start = new Date(holiday.date);
        start.setHours(0, 0, 0, 0);

        // Set the end time to 23:59:59
        const end = new Date(holiday.date);
        end.setHours(23, 59, 59, 999);

        return {
          ...holiday,
          id: index,
          title: holiday.name,
          start,
          end,
          type: 'holiday',
          allDay: true,
        };
      });
    });
  });

  const [firstWorkingDay, workingHours, workingType, leaves] = useWatch({
    control,
    name: ['first_working_day', 'working_hours', 'working_type', 'leaves'],
  });

  // coefficient of working hours per day/week/month
  function getCoef(wh: { label: string; value: number }, wt: { label: string; value: string }) {
    if (wt.value === 'by_day') return wh.value > 4 ? 1 : wh.value / 8;
    if (wt.value === 'by_week') return wh.value / 5 / 8;
    if (wt.value === 'by_month') return wh.value / 20 / 8;

    return 1;
  }

  // check whether a given date is an official holiday
  function isHoliday(date: Date, holidays: ILeaveData[]): boolean {
    return holidays.some(holiday => {
      const holidayDate = new Date(holiday.start);
      return (
        date.getFullYear() === holidayDate.getFullYear() &&
        date.getMonth() === holidayDate.getMonth() &&
        date.getDate() === holidayDate.getDate()
      );
    });
  }

  // working days in the month
  function getWorkingDaysInMonth(date: Date, holidays: ILeaveData[]): number {
    const days = eachDayOfInterval({
      start: startOfMonth(date),
      end: endOfMonth(date),
    });

    return days.filter(day => {
      const weekDay = getDay(day); // 0 = Sunday, 6 = Saturday
      return weekDay !== 0 && weekDay !== 6 && !isHoliday(day, holidays);
    }).length;
  }

  // working days until the end of the month
  function getWorkingDaysUntilEndOfMonth(startDate: Date, holidays: ILeaveData[]): number {
    const days = eachDayOfInterval({
      start: startDate,
      end: endOfMonth(startDate),
    });

    return days.filter(day => {
      const weekDay = getDay(day);
      return weekDay !== 0 && weekDay !== 6 && !isHoliday(day, holidays);
    }).length;
  }

  function calculateLeave(
    fwd: Date,
    wh: { label: string; value: number },
    wt: any,
    lv: number,
    holidays: ILeaveData[]
  ) {
    const coefficient = getCoef(wh, wt);

    const leaveDaysByMonth = (lv * coefficient) / 12;

    const workingMonthsUntilEndOfYear = 12 - (fwd.getMonth() + 1);

    const leaveUntilEndOfTheYear = workingMonthsUntilEndOfYear * leaveDaysByMonth;
    const workingDaysUntilEndOfMonth = getWorkingDaysUntilEndOfMonth(fwd, holidays);
    const workingDaysInMonth = getWorkingDaysInMonth(fwd, holidays);
    const leaveCurrentMonth = (leaveDaysByMonth * workingDaysUntilEndOfMonth) / workingDaysInMonth;

    return Math.round(leaveUntilEndOfTheYear + leaveCurrentMonth);
  }

  const handleCalculateLeave = () => {
    const calculated = calculateLeave(firstWorkingDay, workingHours, workingType, leaves, holidaysData);

    setValue('leave_days_5', calculated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const selectedGroups = watch('groups') || [];

  const mainGroupOptions = selectedGroups.map((group: any) => ({
    value: group.value,
    label: group.label,
  }));

  const [leaveDays4, setLeaveDays4] = useState(watch('leave_days_4') || 0);
  const [wthLeavesMax, setWthLeavesMax] = useState<number | undefined>(undefined);

  const wthLeavesPeriod = watch('wth_leaves_period');

  useEffect(() => {
    if (wthLeavesPeriod?.value === 'weekly') {
      setWthLeavesMax(7);
    } else if (wthLeavesPeriod?.value === 'monthly') {
      setWthLeavesMax(31);
    } else {
      setWthLeavesMax(undefined);
    }
  }, [wthLeavesPeriod]);

  // loading and searching nkpd codes
  const filterCodes = (inputValue: string) => {
    if (inputValue.length < 1) {
      return [];
    }
    const typedNkpdCodes: CodeTuple[] = nkpdCodes as CodeTuple[];
    return typedNkpdCodes
      .filter(
        (code: CodeTuple) => code[1].toLowerCase().includes(inputValue.toLowerCase()) || code[0].includes(inputValue)
      )
      .slice(0, 20)
      .map((code: CodeTuple) => ({
        value: code[0],
        label: `${code[0]} - ${code[1]}`,
      }));
  };

  const loadOptions = (inputValue: string, callback: (options: any[]) => void) => {
    callback(filterCodes(inputValue));
  };

  const handleSelectChange = (option: any) => {
    setValue('nkpd_code', option);
  };

  // watch to enable re-render when employee type is changed
  const employeeType = watch('type') as ISelectOptionProps;
  const userInfo = getUserInfo();
  const isOperator = getSubdomain() === 'operators';
  const isEN = language === 'en';
  const {
    featureLeavesHalfDayOption = false,
    featureLeaveDaysServedTime = false,
    featureLeaveDaysIrregularWorkingHours = false,
    featureLeaveDaysSpecificWorkingConditions = false,
    featureLeaveDaysAdditionalLeave = false,
    featureLimitWfhLeaveDays = false,
  } = userInfo;

  // eslint-disable-next-line prefer-const
  let val1 = watch('leave_days_1');
  // eslint-disable-next-line prefer-const
  let val2 = watch('leave_days_2');
  // eslint-disable-next-line prefer-const
  let val3 = watch('leave_days_3');
  // eslint-disable-next-line prefer-const
  let val4 = watch('leave_days_4');
  // eslint-disable-next-line prefer-const
  let val5 = watch('leave_days_5');

  const emailValue = watch('email');
  const initialEmailRef = useRef(null);

  useEffect(() => {
    if (initialEmailRef.current === null) {
      // Save the initial value when the component mounts
      initialEmailRef.current = emailValue;
    } else if (emailValue !== initialEmailRef.current && isEdit) {
      // Show the message only when the current value differs from the initial value
      setShowMessage(true);
    }
  }, [emailValue]);

  useEffect(() => {
    setTimeout(() => {
      const weekLeaves = parseFloat(methods.getValues('allowed_wfh_leaves_per_week') || '0');
      const monthLeaves = parseFloat(methods.getValues('allowed_wfh_leaves_per_month') || '0');

      if (weekLeaves > monthLeaves) {
        methods.setValue('wth_leaves', weekLeaves);
        methods.setValue('wth_leaves_period', { label: t('structure.employees.perWeek'), value: 'weekly' });
      } else if (monthLeaves > 0) {
        methods.setValue('wth_leaves', monthLeaves);
        methods.setValue('wth_leaves_period', { label: t('structure.employees.perMonth'), value: 'monthly' });
      }
    }, 0);
  }, [methods, t]);

  useEffect(() => {
    const subscription = watch((value: any) => {
      if (value.custom_field_1 === 'null') {
        setValue('custom_field_1', '');
      }
      if (value.custom_field_2 === 'null') {
        setValue('custom_field_2', '');
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    const leaveDaysArr = [String(val1), String(val2), String(val3), String(val4)];

    setLeaveDaysValue(leaveDaysArr, featureLeavesHalfDayOption, setValue);
  }, [val1, val2, val3, val4, featureLeavesHalfDayOption, setValue]);

  /**
   * Mapping if employee type field should be disabled
   * depending on type of employee and granted permissions
   */
  const shouldDisableTypeField: { [key: string]: boolean } = {
    employer: !checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYERS),
    employer_employee:
      !checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYERS) || !checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES),
    moderator: !checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS),
    moderator_employee:
      !checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS) || !checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES),
    employee: !checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES),
  };

  const isEmployee = employeeType?.value.toString().includes('employee');
  const isEmployer = employeeType?.value.toString().includes('employer');
  const isModerator = employeeType?.value.toString().includes('moderator');
  const isEmployerEmployee = employeeType?.value.toString().includes('employer_employee');
  const isLeaveFieldDirty = getIsLeaveFieldDirty(methods.formState.touchedFields).length > 0;
  const isLeavesModuleActive = isOperator ? client.leaves : userInfo.featureLeavesIsActive;
  const isHealthAndSafetyTrainingModuleActive = isOperator
    ? client.is_health_and_safety_training
    : userInfo.featureHealthAndSafetyTraining;
  const isWorkTripsModuleActive = isOperator ? client.is_work_trips : userInfo.featureWorkTrips;
  const isOrganizeGroupsModuleActive = isOperator && client.permissions_scope;
  const isGroupRoleLimitsModuleActive = isOperator
    ? client.group_role_limits_is_active || userInfo.featureGroupRoleLimitsIsActive
    : userInfo.featureGroupRoleLimitsIsActive;
  const isMunicipality = userDataOptions.client_type === 'municipality';
  const employeeTypeValue = employeeType?.value?.toString();
  const isTypeEmployeeOrModeratorEmployee =
    employeeTypeValue === 'employee' || employeeTypeValue === 'moderator_employee';
  const initialEmployeeTypeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (initialEmployeeTypeRef.current === undefined && employeeTypeValue) {
      initialEmployeeTypeRef.current = employeeTypeValue;
    }
  }, [employeeTypeValue]);

  const isChangedFromModeratorToEmployeeType =
    initialEmployeeTypeRef.current === 'moderator' && isTypeEmployeeOrModeratorEmployee;
  const showEgnField =
    (isEmployee || isEmployer || isMunicipality) && (!hasHiddenSensitiveFields || isChangedFromModeratorToEmployeeType);
  const showSalaryBrutoField =
    (isEmployee || (isMunicipality && isModerator)) &&
    (!hasHiddenSensitiveFields || isChangedFromModeratorToEmployeeType);

  const isClientF = userInfo.featureCustomFeatureForClientF;
  const positionLevel = watch('position_level') as ISelectOptionProps;
  const isSubstituteEmployeeActive = userInfo.featureLeaveSubstituteEmployee;
  const isCandidate = employeeType?.value === 'employee_candidate';

  const currentYear = new Date().getFullYear();
  const leaveYearOffsets = getLeaveYears();
  const leaveYears = leaveYearOffsets.map(offset => currentYear + offset);

  const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const compareDateWithToday = (date: Date | null | undefined) => {
    if (!date) return null;

    const today = getTodayDate();
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    if (compareDate.getTime() === today.getTime()) return 'same';
    if (compareDate.getTime() < today.getTime()) return 'past';
    return 'future';
  };

  const shouldValidateReleasedStatus = (data: IInputs) => {
    const isChangingToReleased = data.account_status?.value === 'released';
    const isEmployeeType = isEmployee || data.type?.value.toString().includes('employee');

    return isChangingToReleased && isEmployeeType;
  };

  const scrollToLastWorkingDayField = () => {
    setTimeout(() => {
      const element = document.getElementById('formGroupProbationPeople');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = element.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    }, 100);
  };

  const showReleasedStatusModal = (modalType: 'empty' | 'past' | 'future') => {
    if (modalType === 'empty') {
      const customButtons = (
        <div
          className="d-flex flex-wrap justify-content-center align-items-center"
          style={{ gap: '12px' }}
        >
          <Button
            btnType="primary"
            style={{ minWidth: '200px' }}
            onClick={() => handleModalResponse('use_today')}
          >
            {t('structure.employees.releasedStatusValidation.emptyDateUseToday')}
          </Button>
          <Button
            btnType="secondary"
            style={{ minWidth: '200px' }}
            onClick={() => handleModalResponse('manual_edit')}
          >
            {t('structure.employees.releasedStatusValidation.emptyDateManualEdit')}
          </Button>
          <Button
            btnType="light"
            outlined
            style={{ minWidth: '200px' }}
            onClick={() => handleModalResponse('proceed_empty')}
          >
            {t('structure.employees.releasedStatusValidation.emptyDateLeaveEmpty')}
          </Button>
        </div>
      );

      showModal({
        type: 'success',
        size: 'lg',
        heading: t(`structure.employees.releasedStatusValidation.${modalType}DateTitle`),
        content: t(`structure.employees.releasedStatusValidation.${modalType}DateMessage`),
        successButton: customButtons,
        hideIcon: true,
      });
    } else {
      const customButtons = (
        <div
          className="d-flex justify-content-center"
          style={{ gap: '12px' }}
        >
          <Button
            btnType="primary"
            onClick={() => handleModalResponse(`proceed_${modalType}`)}
          >
            {t(`structure.employees.releasedStatusValidation.${modalType}DateConfirm`)}
          </Button>
          <Button
            btnType="secondary"
            outlined
            onClick={() => handleModalResponse('cancel_edit')}
          >
            {t(`structure.employees.releasedStatusValidation.${modalType}DateEdit`)}
          </Button>
        </div>
      );

      showModal({
        type: 'success',
        size: 'lg',
        heading: t(`structure.employees.releasedStatusValidation.${modalType}DateTitle`),
        content: t(`structure.employees.releasedStatusValidation.${modalType}DateMessage`),
        successButton: customButtons,
        hideIcon: true,
      });
    }
  };

  const handleModalResponse = async (action: string) => {
    hideModal();

    switch (action) {
      case 'use_today':
        setValue('last_working_day', new Date());
        setHasUserChosenDateOption(true);
        setTimeout(async () => {
          const currentData = methods.getValues();
          const formattedData = {
            ...currentData,
            main_group_id: currentData.main_group_id ? currentData.main_group_id.value : null,
          };
          await handleSubmit(formattedData);
        }, 50);
        break;

      case 'manual_edit':
        setHasUserChosenDateOption(true);
        scrollToLastWorkingDayField();
        break;

      case 'proceed_empty':
      case 'proceed_past':
      case 'proceed_future':
        setHasUserChosenDateOption(true);
        setTimeout(async () => {
          const currentData = methods.getValues();
          const formattedData = {
            ...currentData,
            main_group_id: currentData.main_group_id ? currentData.main_group_id.value : null,
          };
          await handleSubmit(formattedData);
        }, 50);
        break;

      case 'cancel_edit':
        setHasUserChosenDateOption(true);
        scrollToLastWorkingDayField();
        break;

      default:
        break;
    }
  };

  const handleFormattedSubmit = async (data: IInputs) => {
    const formattedData = {
      ...data,
      main_group_id: data.main_group_id ? data.main_group_id.value : null,
    };

    if (shouldValidateReleasedStatus(data) && !hasUserChosenDateOption) {
      const lastWorkingDay = data.last_working_day;
      const dateComparison = compareDateWithToday(lastWorkingDay);

      if (!lastWorkingDay) {
        showReleasedStatusModal('empty');
        return;
      }

      if (dateComparison === 'same') {
        await handleSubmit(formattedData);
        return;
      }

      if (dateComparison === 'past') {
        showReleasedStatusModal('past');
        return;
      }

      if (dateComparison === 'future') {
        showReleasedStatusModal('future');
        return;
      }
    }

    await handleSubmit(formattedData);
  };

  useEffect(() => {
    if (!isEmployee) {
      if (!isEmployer) unregister(['egn']);
      unregister([
        'groups',
        'main_group_id',
        'id_card_number',
        'id_card_issued_at',
        'id_card_expires_at',
        'id_card_city',
        'education',
        'address',
        'current_address',
        'mobile_phone',
        'nkpd_code',
        'salary_bruto',
        'bonus',
        'payment_deadline',
        'working_hours',
        'working_type',
        'place_of_work',
        'work_place',
        'leaves',
        'additional_leaves',
        'leave_days_1',
        'leave_days_2',
        'leave_days_3',
        'leave_days_4',
        'leave_days_5',
        'contract_number',
        'leave_notice_deadline',
        'probation_period',
        'probation_beneficiary',
        'other_information',
      ]);
    } else {
      register('groups');
      register('main_group_id');
      if (showEgnField) {
        register('egn');
      }
      if (showSalaryBrutoField) {
        register('salary_bruto');
      }
      if (!isEdit) {
        const groups = userDataOptions.group.filter(gr => gr.selected);
        setValue('groups', groups);
      }
    }

    if (!isEmployer) {
      unregister('is_representative');

      if (isMunicipality) {
        register('legal_relationship');
        register('directorate');
        register('department');
        register('administrative_unit');
        register('position_level');
        register('position_level_info');
        register('position_level_kda');
        register('educational_level');
        register('rang');
        register('monthly_salary_level');
        register('monthly_salary_level_degree');
        register('individual_monthly_salary');
      }
    }

    if (!isModerator)
      unregister([
        'group_rights',
        'can_approve_documents',
        'can_sign_documents',
        'can_manage_employees',
        'can_manage_moderators',
        'can_manage_groups',
        'can_manage_roles',
        'can_manage_document_permissions',
      ]);

    if (!(isModerator || isEmployer || isEmployerEmployee)) {
      unregister(['roles']);
    }
  }, [employeeType]);

  const [showMessage, setShowMessage] = useState(false);

  const [hasUserChosenDateOption, setHasUserChosenDateOption] = useState(false);

  useEffect(() => {
    const subscription = methods.watch((value: any) => {
      setLeaveDays4(value.leave_days_4);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const [showGroupWarning, setShowGroupWarning] = useState(false);

  useEffect(() => {
    const currentMainGroup = methods.getValues('main_group_id');

    if (!isEdit) {
      if (selectedGroups.length === 0 && currentMainGroup) {
        setValue('main_group_id', {});
      } else if (selectedGroups.length === 1 && currentMainGroup?.value !== selectedGroups[0].value) {
        setValue('main_group_id', selectedGroups[0]);
      } else if (selectedGroups.length === 2) {
        const nonCommonGroup = selectedGroups.find((group: ISelectOptionProps) => group.label !== 'Обща група');
        if (nonCommonGroup && currentMainGroup?.value !== nonCommonGroup.value) {
          setValue('main_group_id', nonCommonGroup);
        } else if (!nonCommonGroup && currentMainGroup?.value !== selectedGroups[0].value) {
          setValue('main_group_id', selectedGroups[0]);
        }
      }
    } else {
      if (selectedGroups.length === 0 && currentMainGroup) {
        setValue('main_group_id', {});
      }
      if (
        currentMainGroup &&
        !selectedGroups.some((group: ISelectOptionProps) => group.value === currentMainGroup.value)
      ) {
        setValue('main_group_id', {});
        setShowGroupWarning(true);
      }
    }
  }, [selectedGroups, isEdit, setValue, methods]);

  useEffect(() => {
    setValue('position_level_info', null);
  }, [positionLevel]);

  useEffect(() => {
    return () => {
      setHasUserChosenDateOption(false);
    };
  }, []);

  if (isCandidate) {
    return (
      <Form
        title={title}
        methods={methods}
        onSubmit={handleFormattedSubmit}
        btnLabel={btnLabel}
        showGlobalError
        isHorizontal
      >
        <FormGroup
          controlId="formGroupEmployeeType"
          label={t('structure.employees.type')}
          type="select"
          as="select"
          name="type"
          isRequired
          isHorizontal
          selectOptions={userDataOptions.type.map(type => ({
            ...type,
            isDisabled: type.readOnly,
          }))}
          isDisabled={shouldDisableTypeField[employeeType?.value.toString()]}
        />

        <FormGroup
          controlId="formGroupGroups"
          label={t('structure.employees.groups')}
          type="select"
          as="select"
          name="groups"
          isMulti
          isHorizontal
          allowSelectAll
          selectOptions={userDataOptions.group}
          isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
        />

        <FormGroup
          controlId="formGroupMainGroup"
          label={t('structure.employees.mainGroup')}
          type="select"
          as="select"
          name="main_group_id"
          isHorizontal
          isClearable
          selectOptions={mainGroupOptions}
          isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES) || mainGroupOptions.length === 0}
        />

        <hr />

        <FormGroup
          controlId="formGroupEmail"
          label="E-mail"
          type="email"
          name="email"
          isRequired
          isHorizontal
        />
        <FormGroup
          controlId="formGroupEGN"
          label={t('structure.employees.egn')}
          type="text"
          name="egn"
          placeholder="1234567890"
          isEGN
          isRequired
          isHorizontal
        />
        <FormGroup
          controlId="formGroupFirstName"
          label={t(`${isEN ? 'structure.employees.name_en' : 'structure.employees.name'}`)}
          type="text"
          name="first_name"
          isRequired
          isHorizontal
        />
        <FormGroup
          controlId="formGroupMiddleName"
          label={t(`${isEN ? 'structure.employees.middleName_en' : 'structure.employees.middleName'}`)}
          type="text"
          name="middle_name"
          isHorizontal
        />
        <FormGroup
          controlId="formGroupLastName"
          label={t(`${isEN ? 'structure.employees.lastName_en' : 'structure.employees.lastName'}`)}
          type="text"
          name="last_name"
          isRequired
          isHorizontal
        />

        <FormGroup
          controlId="formGroupAddress"
          label={t(`${isEN ? 'structure.employees.address_en' : 'structure.employees.address'}`)}
          type="text"
          name="address"
          isRequired
          isHorizontal
        />

        <FormGroup
          controlId="formGroupPosition"
          label={t(`${isEN ? 'structure.employees.position_en' : 'structure.employees.position'}`)}
          type="text"
          name="position"
          isRequired
          isHorizontal
        />

        <FormGroup
          controlId="formGroupOtherInfo"
          label={
            isMunicipality
              ? t('structure.employees.otherInfo')
              : t(`${isEN ? 'structure.employees.other_en' : 'structure.employees.other'}`)
          }
          type="text"
          name="other_information"
          isHorizontal
          isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
        />

        <hr />

        <FormGroup
          controlId="formGroupStatus"
          label={t('structure.employees.status')}
          type="select"
          as="select"
          name="account_status"
          isRequired
          isHorizontal
          selectOptions={userDataOptions.account_status}
          isDisabled={shouldDisableTypeField[employeeType?.value.toString()]}
        />

        <FormGroup
          controlId="formGroupReset2FA"
          label={t('structure.employees.twoFaReset')}
          checkType="checkbox"
          name="reset_2fa"
          isHorizontal
          options={[
            {
              label: '',
              name: 'reset_2fa',
              id: 'reset_2fa',
              value: 'true',
            },
          ]}
        />
      </Form>
    );
  }

  return (
    <Form
      title={title}
      methods={methods}
      onSubmit={handleFormattedSubmit}
      btnLabel={btnLabel}
      showGlobalError
      isHorizontal
    >
      <FormGroup
        controlId="formGroupEmployeeType"
        label={t('structure.employees.type')}
        type="select"
        as="select"
        name="type"
        isRequired
        isHorizontal
        selectOptions={userDataOptions.type.map(type => ({
          ...type,
          isDisabled: type.readOnly,
        }))}
        isDisabled={shouldDisableTypeField[employeeType?.value.toString()]}
      />
      {isEmployee && (
        <>
          <FormGroup
            controlId="formGroupGroups"
            label={t('structure.employees.groups')}
            type="select"
            as="select"
            name="groups"
            isMulti
            isHorizontal
            allowSelectAll
            selectOptions={userDataOptions.group}
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
          <FormGroup
            controlId="formGroupMainGroup"
            label={t('structure.employees.mainGroup')}
            type="select"
            as="select"
            name="main_group_id"
            isHorizontal
            isClearable
            selectOptions={mainGroupOptions}
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES) || mainGroupOptions.length === 0}
          />
          {showGroupWarning && (
            <FormRowGroup>
              <Row className="mb-3 ml-1">
                <Col xs={{ span: 'auto', offset: 0 }}>
                  <FaInfoCircle
                    size={15}
                    style={{ color: '#ef476f', marginBottom: '2px' }}
                  />
                  <p
                    style={{
                      color: '#ef476f',
                      display: 'inline',
                      paddingLeft: '5px',
                    }}
                  >
                    {t('structure.employees.mainGroupWarning')}
                  </p>
                </Col>
              </Row>
            </FormRowGroup>
          )}
        </>
      )}
      {isModerator && (
        <>
          <FormGroup
            controlId="formGroupGroupRights"
            label={t('structure.employees.groupRights')}
            type="select"
            as="select"
            name="group_rights"
            isMulti
            isHorizontal
            allowSelectAll
            selectOptions={userDataOptions.group}
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
          />

          <FormGroup
            controlId="formGroupRoles"
            label={t('structure.employees.roles')}
            type="select"
            as="select"
            name="roles"
            isMulti
            isHorizontal
            allowSelectAll
            selectOptions={roleOptions}
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
          />
        </>
      )}
      {isEmployer && (
        <FormGroup
          controlId="formGroupRoles"
          label={t('structure.employees.roles')}
          type="select"
          as="select"
          name="roles"
          isMulti
          isHorizontal
          allowSelectAll
          selectOptions={roleOptions}
        />
      )}
      <FormGroup
        controlId="formGroupEmail"
        label="E-mail"
        type="email"
        name="email"
        isRequired
        isHorizontal
      />
      {showMessage && (
        <Row className="mb-3 offset-md-2 col-sm-6">
          <Col xs={{ span: 'auto', offset: 0 }}>
            <FaInfoCircle
              size={15}
              style={{ color: '#ef476f', marginBottom: '2px' }}
            />
            <p
              style={{
                color: '#ef476f',
                display: 'inline',
                paddingLeft: '5px',
              }}
            >
              {t('structure.employees.additionalEmailHint')}
            </p>
          </Col>
        </Row>
      )}
      {(isSubstituteEmployeeActive || isModerator) && (
        <>
          <Row className="offset-md-2">
            <Col md={5}>
              {isSubstituteEmployeeActive && (
                <>
                  <FormGroup
                    controlId="formGroupAlwaysEligibleAsSubstitute"
                    label={t('structure.employees.alwaysEligibleAsSubstitute')}
                    checkType="switch"
                    name="always_eligible_as_substitute"
                    isRequired
                  />
                </>
              )}

              {isModerator && (
                <>
                  <FormGroup
                    controlId="formGroupCanApproveDocs"
                    label={t('structure.employees.approveDocs')}
                    checkType="switch"
                    name="can_approve_documents"
                    isRequired
                    isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                  />
                  <p className="custom-switch__desc text-muted small mb-2">
                    {t('structure.employees.approveDocsDesc')}
                  </p>

                  <FormGroup
                    controlId="formGroupCanManageEmployees"
                    label={t('structure.employees.manageEmployees')}
                    checkType="switch"
                    name="can_manage_employees"
                    isRequired
                    isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                  />
                  <p className="custom-switch__desc text-muted small mb-2">
                    {t('structure.employees.manageEmployeesDesc')}
                  </p>

                  <FormGroup
                    controlId="formGroupCanManageGroups"
                    label={t('structure.employees.manageGroups')}
                    checkType="switch"
                    name="can_manage_groups"
                    isRequired
                    isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                  />
                  <p className="custom-switch__desc text-muted small mb-2">
                    {t('structure.employees.manageGroupsDesc')}
                  </p>

                  <FormGroup
                    controlId="formGroupCanManageDocsPermissions"
                    label={t('structure.employees.manageDocPermissions')}
                    checkType="switch"
                    name="can_manage_document_permissions"
                    isRequired
                    isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                  />
                  <p className="custom-switch__desc text-muted small mb-2">
                    {t('structure.employees.manageDocPermissionsDesc')}
                  </p>

                  {methods.watch('can_manage_employees') && (
                    <>
                      <FormGroup
                        controlId="formGroupCanDownloadUserDataReport"
                        label={t('structure.employees.downloadUserDataReport')}
                        checkType="switch"
                        name="can_download_user_data_report"
                        isRequired
                        isDisabled={
                          !checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS) ||
                          !checkPermission(PermissionKeys.CAN_MANAGE_DOCUMENT_PERMISSIONS)
                        }
                      />
                      <p className="custom-switch__desc text-muted small mb-2">
                        {t('structure.employees.downloadUserDataReportDesc')}
                      </p>
                    </>
                  )}

                  {methods.watch('can_download_user_data_report') && (
                    <>
                      <FormGroup
                        controlId="formGroupCanViewSensitiveFieldsOnUserDataReport"
                        label={t('structure.employees.viewSensitiveFieldsOnUserDataReport')}
                        checkType="switch"
                        name="can_view_sensitive_fields_on_user_data_report"
                        isRequired
                        isDisabled={
                          !checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS) ||
                          !checkPermission(PermissionKeys.CAN_MANAGE_DOCUMENT_PERMISSIONS)
                        }
                      />
                      <p className="custom-switch__desc text-muted small mb-2">
                        {t('structure.employees.viewSensitiveFieldsOnUserDataReportDesc')}
                      </p>
                    </>
                  )}
                </>
              )}
            </Col>

            <Col md={5}>
              {isModerator && (
                <>
                  <FormGroup
                    controlId="formGroupCanSignDocs"
                    label={t('structure.employees.signDocs')}
                    checkType="switch"
                    name="can_sign_documents"
                    isRequired
                    isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                  />
                  <p className="custom-switch__desc text-muted small mb-2">{t('structure.employees.signDocsDesc')}</p>

                  <FormGroup
                    controlId="formGroupCanManageModerators"
                    label={t('structure.employees.manageModerators')}
                    checkType="switch"
                    name="can_manage_moderators"
                    isRequired
                    isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                  />
                  <p className="custom-switch__desc text-muted small mb-2">
                    {t('structure.employees.manageModeratorsDesc')}
                  </p>

                  <FormGroup
                    controlId="formGroupCanManageRoles"
                    label={t('structure.employees.manageRoles')}
                    checkType="switch"
                    name="can_manage_roles"
                    isRequired
                    isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                  />
                  <p className="custom-switch__desc text-muted small mb-2">
                    {t('structure.employees.manageRolesDesc')}
                  </p>

                  {isLeavesModuleActive && (
                    <>
                      <FormGroup
                        controlId="formGroupCanManageLeaves"
                        label={t('structure.employees.manageLeaves')}
                        checkType="switch"
                        name="can_manage_leaves"
                        isRequired
                        isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                      />
                      <p className="custom-switch__desc text-muted small mb-2">
                        {t('structure.employees.manageLeavesDesc')}
                      </p>
                    </>
                  )}

                  {isHealthAndSafetyTrainingModuleActive && (
                    <>
                      <FormGroup
                        controlId="formGroupCanManageHealthAndSafetyTraining"
                        label={t('structure.employees.manageHealthAndSafetyTraining')}
                        checkType="switch"
                        name="can_manage_health_and_safety_training"
                        isRequired
                        isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                      />
                      <p className="custom-switch__desc text-muted small mb-2">
                        {t('structure.employees.manageHealthAndSafetyTrainingDesc')}
                      </p>
                    </>
                  )}

                  {isWorkTripsModuleActive && (
                    <>
                      <FormGroup
                        controlId="formGroupCanManageWorkTrips"
                        label={t('structure.employees.manageWorkTrips')}
                        checkType="switch"
                        name="can_manage_work_trips"
                        isRequired
                        isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_MODERATORS)}
                      />
                      <p className="custom-switch__desc text-muted small mb-2">
                        {t('structure.employees.manageWorkTripsDesc')}
                      </p>
                    </>
                  )}
                </>
              )}
            </Col>
          </Row>
        </>
      )}
      {isEdit && isOperator && (isOrganizeGroupsModuleActive || isGroupRoleLimitsModuleActive) && (
        <div
          className="offset-sm-2 d-flex flex-wrap"
          style={{ gap: '10px', marginTop: '15px' }}
        >
          {isModerator && isOrganizeGroupsModuleActive && (
            <Button
              btnType="primary"
              onClick={() => setIsOrganizeGroup && setIsOrganizeGroup(true)}
            >
              {t('structure.employees.manageRights')}
            </Button>
          )}
          {(isModerator || isEmployer || isEmployerEmployee) && isGroupRoleLimitsModuleActive && (
            <Button
              btnType="primary"
              onClick={() => setIsManageGroupRoleLimits && setIsManageGroupRoleLimits(true)}
            >
              {t('structure.employees.manageGroupRoleLimits')}
            </Button>
          )}
        </div>
      )}
      <hr style={{ width: '66%', marginLeft: '0' }} />
      <FormGroup
        controlId="formGroupFirstName"
        label={t(`${isEN ? 'structure.employees.name_en' : 'structure.employees.name'}`)}
        type="text"
        name="first_name"
        isRequired
        isHorizontal
      />
      <FormGroup
        controlId="formGroupMiddleName"
        label={t(`${isEN ? 'structure.employees.middleName_en' : 'structure.employees.middleName'}`)}
        type="text"
        name="middle_name"
        isHorizontal
      />
      <FormGroup
        controlId="formGroupLastName"
        label={t(`${isEN ? 'structure.employees.lastName_en' : 'structure.employees.lastName'}`)}
        type="text"
        name="last_name"
        isRequired
        isHorizontal
      />
      {isMunicipality && (isEmployee || isModerator) && (
        <FormGroup
          controlId="formGroupDateOfBirth"
          label={t('structure.employees.dateOfBirth')}
          type="datepicker"
          name="date_of_birth"
          dateFormat="dd.MM.yyyy"
          maxDate={new Date()}
          isHorizontal
          showMonthDropdown
          showYearDropdown
        />
      )}
      {showEgnField && (
        <FormGroup
          controlId="formGroupEGN"
          label={t('structure.employees.egn')}
          type="text"
          name="egn"
          placeholder="1234567890"
          isEGN
          isRequired={isEmployee && (!hasHiddenSensitiveFields || isChangedFromModeratorToEmployeeType)}
          isHorizontal
          isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
        />
      )}
      {isEmployee && (
        <>
          {!hasHiddenSensitiveFields && (
            <FormGroup
              controlId="formGroupIdCardNumber"
              label={t('structure.employees.IDCardNumber')}
              type="text"
              name="id_card_number"
              hint={t('structure.employees.IDCardNumberHint')}
              placeholder="123456789"
              isIdCard
              isHorizontal
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
          )}
          <FormRowGroup>
            <FormGroup
              controlId="formGroupIdCardIssuedAt"
              label={t('structure.employees.IDCardIssuedAt')}
              type="datepicker"
              name="id_card_issued_at"
              dateFormat="dd.MM.yyyy"
              maxDate={new Date()}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={15}
              isRowGroup
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
            <FormGroup
              controlId="formGroupIdCardExpiresAt"
              label={t('structure.employees.IDCardExpiresAt')}
              type="datepicker"
              name="id_card_expires_at"
              dateFormat="dd.MM.yyyy"
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={15}
              isRowGroup
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
          </FormRowGroup>
          <FormGroup
            controlId="formGroupIdCardCity"
            label={t(
              `${isEN ? 'structure.employees.IDCardIssuedInCity_en' : 'structure.employees.IDCardIssuedInCity'}`
            )}
            type="text"
            name="id_card_city"
            isHorizontal
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
          <FormGroup
            controlId="formGroupEducation"
            label={t('structure.employees.education')}
            type="select"
            as="select"
            name="education"
            isClearable
            isHorizontal
            selectOptions={userDataOptions.education}
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
        </>
      )}
      {isMunicipality && (isEmployee || isModerator) && (
        <FormGroup
          controlId="formGroupEducationalLevel"
          label={t('structure.employees.educationalLevel')}
          type="select"
          as="select"
          name="educational_level"
          isClearable
          isHorizontal
          selectOptions={userDataOptions.educational_level}
        />
      )}
      {isEmployee && (
        <>
          <FormGroup
            controlId="formGroupAddress"
            label={t(`${isEN ? 'structure.employees.address_en' : 'structure.employees.address'}`)}
            type="text"
            name="address"
            isRequired
            isHorizontal
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
          <FormGroup
            controlId="formGroupCurrentAddress"
            label={t(`${isEN ? 'structure.employees.currentAddress_en' : 'structure.employees.currentAddress'}`)}
            type="text"
            name="current_address"
            isRequired
            isHorizontal
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
          <FormGroup
            controlId="formGroupPhone"
            label={t('structure.employees.mobilePhone')}
            type="text"
            name="mobile_phone"
            hint={t('structure.employees.mobilePhoneHint')}
            isRequired={isClientF}
            isHorizontal
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
        </>
      )}
      {isMunicipality && (isEmployee || isModerator) && (
        <>
          <FormGroup
            controlId="formGroupLegalRelationship"
            label={t('structure.employees.legalRelationship')}
            type="select"
            as="select"
            name="legal_relationship"
            isClearable
            isHorizontal
            selectOptions={userDataOptions.legal_relationship}
          />
          <FormGroup
            controlId="formGroupDirectorate"
            label={t('structure.employees.directorate')}
            type="text"
            name="directorate"
            isHorizontal
          />
          <FormGroup
            controlId="formGroupDepartment"
            label={t('structure.employees.department')}
            type="text"
            name="department"
            isHorizontal
          />
          <FormGroup
            controlId="formGroupAdministrativeUnit"
            label={t('structure.employees.administrativeUnit')}
            type="select"
            as="select"
            name="administrative_unit"
            isClearable
            isHorizontal
            selectOptions={userDataOptions.administrative_unit}
          />
          <FormGroup
            controlId="formGroupPositionLevel"
            label={t('structure.employees.positionLevel')}
            type="select"
            as="select"
            name="position_level"
            isClearable
            isHorizontal
            selectOptions={userDataOptions.position_level}
          />
          {positionLevel?.value === 'leading' && (
            <FormGroup
              controlId="formGroupPositionLevelLeading"
              label={t('structure.employees.positionLevelLeading')}
              type="select"
              as="select"
              name="position_level_info"
              isClearable
              isHorizontal
              selectOptions={userDataOptions.position_level_leading}
            />
          )}
          {positionLevel?.value === 'expert' && (
            <FormGroup
              controlId="formGroupPositionLevelExpert"
              label={t('structure.employees.positionLevelExpert')}
              type="select"
              as="select"
              name="position_level_info"
              isClearable
              isHorizontal
              selectOptions={userDataOptions.position_level_expert}
            />
          )}
          {positionLevel?.value === 'specialist' && (
            <FormGroup
              controlId="formGroupPositionLevelSpecialist"
              label={t('structure.employees.positionLevelSpecialist')}
              type="select"
              as="select"
              name="position_level_info"
              isClearable
              isHorizontal
              selectOptions={userDataOptions.position_level_specialist}
            />
          )}
          <FormGroup
            controlId="formGroupPositionLevelKDA"
            label={t('structure.employees.positionLevelKDA')}
            type="text"
            name="position_level_kda"
            isHorizontal
          />
        </>
      )}
      <FormGroup
        controlId="formGroupPosition"
        label={t(`${isEN ? 'structure.employees.position_en' : 'structure.employees.position'}`)}
        type="text"
        name="position"
        isRequired={isEmployee}
        isHorizontal
        isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
      />
      {isEmployee && (
        <FormGroup
          controlId="formGroupnkpdCode"
          label={t('structure.employees.nkpdCode')}
          type="select"
          as="select"
          name="nkpd_code"
          isRequired
          isHorizontal
          isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          isAsyncSelect
          loadOptions={loadOptions}
          onChange={handleSelectChange}
          isClearable
        />
      )}
      {isMunicipality && (isEmployee || isModerator) && (
        <>
          <FormGroup
            controlId="formGroupRang"
            label={t('structure.employees.rang')}
            type="select"
            as="select"
            name="rang"
            isClearable
            isHorizontal
            selectOptions={userDataOptions.rang}
          />
          <FormGroup
            controlId="formGroupMonthlySalaryLevel"
            label={t('structure.employees.monthlySalaryLevel')}
            type="select"
            as="select"
            name="monthly_salary_level"
            isClearable
            isHorizontal
            selectOptions={userDataOptions.monthly_salary_level}
          />
          <FormGroup
            controlId="formGroupMonthlySalaryLevelDegree"
            label={t('structure.employees.monthlySalaryLevelDegree')}
            type="select"
            as="select"
            name="monthly_salary_level_degree"
            isClearable
            isHorizontal
            selectOptions={userDataOptions.monthly_salary_level_degree}
          />
        </>
      )}
      {showSalaryBrutoField && (
        <FormGroup
          controlId="formGroupSalaryBruto"
          label={
            isMunicipality ? t('structure.employees.grossSalaryMunicipality') : t('structure.employees.grossSalary')
          }
          type="text"
          name="salary_bruto"
          hint={t('structure.employees.grossSalaryHint')}
          isRequired={!hasHiddenSensitiveFields || isChangedFromModeratorToEmployeeType}
          isNumber
          isHorizontal
          isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
        />
      )}
      {isEmployee && (
        <>
          <FormRowGroup>
            <FormGroup
              controlId="formGroupBonus"
              label={t('structure.employees.bonus')}
              type="number"
              name="bonus"
              hint={t('structure.employees.bonusHint')}
              isRowGroup
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
            <FormGroup
              controlId="formGroupPaymentDeadline"
              label={t('structure.employees.paymentDeadline')}
              type="text"
              name="payment_deadline"
              hint={t('structure.employees.paymentDeadlineHint')}
              isRowGroup
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
          </FormRowGroup>

          <FormRowGroup
            label={t('structure.employees.workingHours')}
            isRequired
          >
            <FormGroup
              controlId="formGroupWorkingHours"
              type="select"
              as="select"
              name="working_hours"
              hint={t('structure.employees.workingHoursHint')}
              isRequired
              isRowGroup
              selectOptions={userDataOptions.working_hours}
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
            <FormGroup
              controlId="formGroupWorkingType"
              type="select"
              as="select"
              name="working_type"
              isRequired
              isRowGroup
              selectOptions={userDataOptions.working_type}
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
          </FormRowGroup>

          <FormGroup
            controlId="formGroupPlaceOfWork"
            label={t(`${isEN ? 'structure.employees.placeOfWork_en' : 'structure.employees.placeOfWork'}`)}
            type="text"
            name="place_of_work"
            hint={t('structure.employees.placeOfWorkHint')}
            isRequired
            isHorizontal
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
          <FormGroup
            controlId="formGroupWorkingPlace"
            label={t(`${isEN ? 'structure.employees.workingPlace_en' : 'structure.employees.workingPlace'}`)}
            type="text"
            name="work_place"
            isRequired={!isClientF}
            isHorizontal
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />

          <FormRowGroup
            label={t('structure.employees.annualLeaves')}
            isRequired={isLeavesModuleActive}
          >
            <FormGroup
              controlId="formGroupLeaves"
              label={t('structure.employees.mainLeaves')}
              type="text"
              min={isLeavesModuleActive ? 1 : undefined}
              name="leaves"
              hint={t('structure.employees.mainLeavesHint')}
              isRowGroup
              isRequired={isLeavesModuleActive}
              isNumber
              isLeaveDaysCount
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
            <FormGroup
              controlId="formGroupAdditionalLeaves"
              label={t('structure.employees.additionalLeaves')}
              type="number"
              name="additional_leaves"
              hint={t('structure.employees.additionalLeavesHint')}
              isRowGroup
              isLeaveDaysCount
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
          </FormRowGroup>
          <FormRowGroup>
            {featureLeaveDaysServedTime && (
              <FormGroup
                controlId="formGroupLeaves"
                label={t('structure.employees.leaveDaysServedTime')}
                type="text"
                min={0}
                name="leave_days_served_time"
                isRowGroup
                isNumber
                isLeaveDaysCount
                isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              />
            )}
            {featureLeaveDaysAdditionalLeave && (
              <FormGroup
                controlId="formGroupLeaves"
                label={t('structure.employees.leaveDaysAdditionalLeave')}
                type="text"
                min={0}
                name="leave_days_paid_additional"
                isRowGroup
                isNumber
                isLeaveDaysCount
                isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              />
            )}
          </FormRowGroup>
          <FormRowGroup>
            {featureLeaveDaysIrregularWorkingHours && (
              <FormGroup
                controlId="formGroupLeaves"
                label={t('structure.employees.leaveDaysIrregularWorkingHours')}
                type="text"
                min={0}
                name="leave_days_irregular_working_hours"
                isRowGroup
                isNumber
                isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              />
            )}
            {featureLeaveDaysSpecificWorkingConditions && (
              <FormGroup
                controlId="formGroupLeaves"
                label={t('structure.employees.leaveDaysSpecificWorkingConditions')}
                type="text"
                min={0}
                name="leave_days_specific_working_conditions"
                isRowGroup
                isNumber
                isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              />
            )}
          </FormRowGroup>
          <FormRowGroup
            label={t('structure.employees.leaveDaysPerYear')}
            isRequired={isLeavesModuleActive}
          >
            <FormGroup
              controlId="formGroupLeaveDays"
              label={String(leaveYears[0])}
              type="text"
              min={0}
              name="leave_days_1"
              isRowGroup
              isLeaveDaysCount
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              isNumber
              hint={
                featureLeavesHalfDayOption ? `${t('leaves.multipleOfHalfRequired')}` : `${t('leaves.integerRequired')}`
              }
            />
            <FormGroup
              controlId="formGroupLeaveDays2"
              label={String(leaveYears[1])}
              type="text"
              min={0}
              name="leave_days_2"
              isRowGroup
              isLeaveDaysCount
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              isNumber
              hint={
                featureLeavesHalfDayOption ? `${t('leaves.multipleOfHalfRequired')}` : `${t('leaves.integerRequired')}`
              }
            />
            <FormGroup
              controlId="formGroupLeaveDays3"
              label={String(leaveYears[2])}
              type="text"
              min={0}
              name="leave_days_3"
              isRowGroup
              isLeaveDaysCount
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              isNumber
              hint={
                featureLeavesHalfDayOption ? `${t('leaves.multipleOfHalfRequired')}` : `${t('leaves.integerRequired')}`
              }
            />
            <FormGroup
              controlId="formGroupLeaveDays4"
              label={String(leaveYears[3])}
              type="text"
              min={0}
              name="leave_days_4"
              isRowGroup
              isLeaveDaysCount
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              isNumber
              hint={
                featureLeavesHalfDayOption ? `${t('leaves.multipleOfHalfRequired')}` : `${t('leaves.integerRequired')}`
              }
              // tooltip={parseInt(leaveDays4, 10) > 0 ? `${t('leaves.tooltipFutureYear')}` : undefined} This should be for next year tooltip.
              // tooltipPosition="right"
            />
            <FormGroup
              controlId="formGroupLeaveDays5"
              label={String(leaveYears[4])}
              type="text"
              min={0}
              name="leave_days_5"
              isRowGroup
              isLeaveDaysCount
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
              isNumber
              hint={
                featureLeavesHalfDayOption ? `${t('leaves.multipleOfHalfRequired')}` : `${t('leaves.integerRequired')}`
              }
              tooltip={!isEdit ? t('structure.employees.leaveDaysTooltip') : undefined}
              tooltipPosition="right"
            />
            {!isEdit && (
              <div className="m-3">
                <TooltipWrapper
                  id="tooltip-calculate-leave"
                  overlay={t('structure.employees.calculateLeaveBtnHint')}
                  wrapperClass="mt-4"
                >
                  <Button
                    btnType="info"
                    type="button"
                    preventDebounce
                    onClick={() => handleCalculateLeave()}
                    disabled={!firstWorkingDay || !workingHours || !leaves}
                  >
                    {t('structure.employees.calculateLeaveBtn')}
                  </Button>
                </TooltipWrapper>
              </div>
            )}
            {isLeaveFieldDirty && isLeavesModuleActive && (
              <Row className="mb-3 ml-1">
                <Col xs={{ span: 'auto', offset: 0 }}>
                  <FaInfoCircle
                    size={15}
                    style={{ color: '#ef476f', marginBottom: '2px' }}
                  />
                  <p
                    style={{
                      color: '#ef476f',
                      display: 'inline',
                      paddingLeft: '5px',
                    }}
                  >
                    {t('structure.employees.additionalLeavesHintForEditedField')}
                  </p>
                </Col>
              </Row>
            )}
          </FormRowGroup>
          <FormRowGroup>
            <FormGroup
              controlId="formGroupFirstWorkingDay"
              label={t('structure.employees.firstWorkingDay')}
              type="datepicker"
              name="first_working_day"
              dateFormat="dd.MM.yyyy"
              isRowGroup
              showMonthDropdown
              showYearDropdown
            />
            <FormGroup
              controlId="formGroupProbationPeople"
              label={t('structure.employees.lastWorkingDay')}
              type="datepicker"
              name="last_working_day"
              dateFormat="dd.MM.yyyy"
              isRowGroup
              showMonthDropdown
              showYearDropdown
            />
          </FormRowGroup>
          {featureLimitWfhLeaveDays && (
            <FormRowGroup label={t('structure.employees.leavesWfh')}>
              <FormGroup
                controlId="formGroupFirstWorkingDay"
                type="text"
                name="wth_leaves"
                isNumber
                min={0}
                max={wthLeavesMax}
                isRowGroup
              />
              <FormGroup
                controlId="formGroupProbationPeople"
                type="select"
                name="wth_leaves_period"
                isRowGroup
                selectOptions={[
                  { label: `${t('structure.employees.perWeek')}`, value: 'weekly' },
                  { label: `${t('structure.employees.perMonth')}`, value: 'monthly' },
                ]}
              />
            </FormRowGroup>
          )}
          <FormGroup
            controlId="formGroupContractNumber"
            label={t('structure.employees.contractNumber')}
            type="text"
            name="contract_number"
            isHorizontal
          />
          <FormGroup
            controlId="formGroupLeaveNoticeDeadline"
            label={t('structure.employees.noticeDeadline')}
            type="select"
            as="select"
            name="leave_notice_deadline"
            isRequired
            isHorizontal
            selectOptions={userDataOptions.leave_notice_deadline}
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
          <FormRowGroup label={t('structure.employees.probationPeriod')}>
            <FormGroup
              controlId="formGroupProbationPeriod"
              label={t('structure.employees.duration')}
              type="select"
              as="select"
              name="probation_period"
              isClearable
              isRowGroup
              selectOptions={userDataOptions.probation_period}
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
            <FormGroup
              controlId="formGroupProbationPeople"
              label={t('structure.employees.inFavorOf')}
              type="select"
              as="select"
              name="probation_beneficiary"
              isClearable
              isRowGroup
              selectOptions={userDataOptions.probation_beneficiary}
              isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
            />
          </FormRowGroup>
          <FormGroup
            controlId="formGroupOtherInfo"
            label={
              isMunicipality
                ? t('structure.employees.otherInfo')
                : t(`${isEN ? 'structure.employees.other_en' : 'structure.employees.other'}`)
            }
            type="text"
            name="other_information"
            isHorizontal
            isDisabled={!checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYEES)}
          />
          {isClientF && (
            <FormRowGroup>
              <FormGroup
                controlId="flutterBeID"
                label="FlutterBe ID"
                type="text"
                name="custom_field_1"
                isNumber
                isRowGroup
                isHorizontal
              />
              <FormGroup
                controlId="payrollID"
                label="Payroll ID"
                type="text"
                name="custom_field_2"
                isNumber
                isRowGroup
                isHorizontal
              />
            </FormRowGroup>
          )}
        </>
      )}
      <hr />
      <FormGroup
        controlId="formGroupStatus"
        label={t('structure.employees.status')}
        type="select"
        as="select"
        name="account_status"
        isRequired
        isHorizontal
        selectOptions={userDataOptions.account_status}
        isDisabled={shouldDisableTypeField[employeeType?.value.toString()]}
      />
      {isEmployer && (
        <FormGroup
          controlId="formGroupRepresentative"
          label={t('structure.employees.representative')}
          checkType="checkbox"
          name="is_representative"
          hint={
            isMunicipality
              ? t('structure.employees.representativeMunicipalityHint')
              : t('structure.employees.representativeHint')
          }
          isHorizontal
          options={[
            {
              label: '',
              name: 'is_representative',
              id: 'is_representative',
              value: 'true',
              disabled: !checkPermission(PermissionKeys.CAN_MANAGE_EMPLOYERS),
            },
          ]}
        />
      )}
      <FormGroup
        controlId="formGroupReset2FA"
        label={t('structure.employees.twoFaReset')}
        checkType="checkbox"
        name="reset_2fa"
        isHorizontal
        options={[
          {
            label: '',
            name: 'reset_2fa',
            id: 'reset_2fa',
            value: 'true',
          },
        ]}
      />
    </Form>
  );
};

export default EmployeeForm;
